import { useMemo } from "react";
import { useDispatch } from "react-redux";
import type { HistoricNarRecommendation } from "../../../../../api/services/historicNar/types";
import CustomTable from "../../../../../components/custom-table/custom-table";
import { HistoricNarIngestionState } from "../../../../../enums/historicNarIngestionState";
import { ModalId } from "../../../../../enums/modalId";
import { setOpenedModalId } from "../../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../../redux/store/store";
import { NarAgentGridOrgTab } from "../../narAgentGrid/hooks/useNarAgentGridOrgTabs";
import {
  generateHistoricNarConfigTableData,
  HistoricNarConfigTableHeaders,
} from "../utils/historicNarConfigTable";

/**
 * Render the Historic NAR recommendations in a `CustomTable`.
 *
 * @param apiData - Recommendation rows to display (already filtered by the parent)
 * @param tableRef - Ref applied to the scroll container
 * @param clientOptions - Organization IDs and labels used in automation tooltips
 * @param onSearchTextChange - Invoked with the built-in search bar's text so the
 *   parent can apply the free-text filter (search is submit/clear driven)
 * @param onRefresh - Invoked when the table's built-in refresh button is clicked
 *   (re-fetches the recommendations)
 */
const HistoricNarConfigTable = ({
  apiData,
  tableRef,
  onSearchTextChange,
  onRefresh,
  clientOptions,
}: {
  apiData: HistoricNarRecommendation[];
  tableRef: React.RefObject<HTMLDivElement>;
  clientOptions: NarAgentGridOrgTab[];
  onSearchTextChange?: (text: string) => void;
  onRefresh?: () => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const tableData = useMemo(
    () => generateHistoricNarConfigTableData(apiData, clientOptions),
    [apiData, clientOptions],
  );

  return (
    <div
      ref={tableRef}
      className="historic-nar-config-table flex h-full flex-col overflow-auto"
    >
      <CustomTable
        tableHeaders={HistoricNarConfigTableHeaders}
        tableData={tableData}
        tableName={"historicNarConfig"}
        itemsPerPage={20}
        pagesPerView={4}
        count={tableData.length}
        totalCount={tableData.length}
        endIndexOfTable={() => {}}
        searchingText={(text: string) => onSearchTextChange?.(text)}
        placeholder="Search by drug name, J-code, or plan"
        hideSearchBar={false}
        isFetching={false}
        showExportButton={false}
        onExportButtonClick={() => {}}
        showInLineLoader={false}
        showRefreshButton={true}
        onRefreshButton={() => onRefresh?.()}
        onIconCellClick={(data) => {
          dispatch(
            setOpenedModalId({
              id:
                data.status === HistoricNarIngestionState.NEEDS_REVIEW
                  ? ModalId.NEEDS_REVIEW_MODAL
                  : ModalId.HISTORIC_NAR_CONFIRM_SOURCE_MODAL,
              metaData: data,
            }),
          );
        }}
      />
    </div>
  );
};

export default HistoricNarConfigTable;
